import {
  createContext,
  type Dispatch,
  type FC,
  type MutableRefObject,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface AgentChatViewportContextValue {
  agentChatToggleRef: MutableRefObject<HTMLButtonElement | null>;
  agentChatWidth: number;
  closeAgentChat: () => void;
  hasAgentChatEntered: boolean;
  isAgentChatMaximized: boolean;
  isAgentChatOpen: boolean;
  isAgentChatResizing: boolean;
  openAgentChat: () => void;
  setAgentChatWidth: Dispatch<SetStateAction<number>>;
  setHasAgentChatEntered: Dispatch<SetStateAction<boolean>>;
  setIsAgentChatMaximized: Dispatch<SetStateAction<boolean>>;
  setIsAgentChatOpen: Dispatch<SetStateAction<boolean>>;
  setIsAgentChatResizing: Dispatch<SetStateAction<boolean>>;
}

export const AGENT_CHAT_DEFAULT_WIDTH = 400;

const AgentChatViewportContext = createContext<AgentChatViewportContextValue | null>(null);

/**
 * Drawer presentation state lives at the root because the floating toggle is
 * rendered by WorkspaceTabs, which remounts on every in-workspace navigation.
 * Keeping open/maximized/entered here means a tab click no longer resets how
 * the drawer is presented.
 */
export const AgentChatViewportProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isAgentChatOpen, setIsAgentChatOpen] = useState(false);
  const [isAgentChatResizing, setIsAgentChatResizing] = useState(false);
  const [isAgentChatMaximized, setIsAgentChatMaximized] = useState(false);
  const [hasAgentChatEntered, setHasAgentChatEntered] = useState(false);
  const [agentChatWidth, setAgentChatWidth] = useState(AGENT_CHAT_DEFAULT_WIDTH);
  const agentChatToggleRef = useRef<HTMLButtonElement | null>(null);

  // Published on the document element rather than a wrapper because the pieces
  // that need to reserve this width are portaled into <body> — the drawer
  // itself and the fullscreen document viewer — so they inherit nothing from
  // the page tree.
  useEffect(() => {
    const { documentElement } = document;

    documentElement.style.setProperty('--agent-chat-width', `${agentChatWidth}px`);
    return () => {
      documentElement.style.removeProperty('--agent-chat-width');
    };
  }, [agentChatWidth]);

  const openAgentChat = useCallback(() => {
    setHasAgentChatEntered(false);
    setIsAgentChatOpen(true);
  }, []);

  const closeAgentChat = useCallback(() => {
    setIsAgentChatOpen(false);
    setIsAgentChatMaximized(false);
    setHasAgentChatEntered(false);
    requestAnimationFrame(() => agentChatToggleRef.current?.focus({ preventScroll: true }));
  }, []);

  const value = useMemo(
    () => ({
      agentChatToggleRef,
      agentChatWidth,
      closeAgentChat,
      hasAgentChatEntered,
      isAgentChatMaximized,
      isAgentChatOpen,
      isAgentChatResizing,
      openAgentChat,
      setAgentChatWidth,
      setHasAgentChatEntered,
      setIsAgentChatMaximized,
      setIsAgentChatOpen,
      setIsAgentChatResizing,
    }),
    [
      agentChatWidth,
      closeAgentChat,
      hasAgentChatEntered,
      isAgentChatMaximized,
      isAgentChatOpen,
      isAgentChatResizing,
      openAgentChat,
    ],
  );

  return (
    <AgentChatViewportContext.Provider value={value}>{children}</AgentChatViewportContext.Provider>
  );
};

export const useAgentChatViewport = () => {
  const context = useContext(AgentChatViewportContext);

  if (!context) {
    throw new Error('Missing AgentChatViewportProvider in tree');
  }

  return context;
};

/**
 * Floats a fixed overlay above the Agent Chat drawer.
 *
 * The right-anchored slide-in panels are positioned against the viewport
 * rather than the page, so the page reflowing around the drawer does nothing
 * for them and they would open underneath it. Rather than reserving room for the chat, they keep their normal
 * position and size and simply stack on top of it.
 *
 * They all start below the app header while the drawer runs the full height, so
 * the drawer's own header stays visible above them and its close and restore
 * buttons remain reachable.
 */
export const useAboveAgentChat = () => {
  const { isAgentChatOpen } = useAgentChatViewport();

  // Important because this competes with each overlay's own z-[50]: both are
  // Tailwind utilities, so layer order cannot separate them.
  return isAgentChatOpen ? '!z-[90]' : undefined;
};
